import { Box, Cyl, Lathe, RBox, Slab } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

const BODY = 0.32
/** Front face of the cabinet — everything on the fascia measures from here. */
const FACE = BODY / 2
const PLINTH_H = 0.05

/** Cabinet height per variant: the bottled unit is short, the mains tower is not. */
const BOTTLED_BODY = 0.72
const MAINS_BODY = 1.05
const BOTTLE_TOP = 1.12

/**
 * Recessed dispensing niche: a dark volume sunk 2 mm behind the fascia, with a
 * metal spout, two levers and a drip grille inside it. `y` is the niche centre.
 */
function DispenseNiche({ y }: { y: number }) {
  const depth = 0.07
  const z = FACE - depth / 2 - 0.002

  return (
    <group>
      <Box tone="dark" size={[0.2, 0.17, depth]} position={[0, y, z]} />
      <Cyl tone="metal" r={0.009} h={0.05} segments={12} position={[0, y + 0.045, z + 0.01]} />
      {[-1, 1].map((side) => (
        <RBox
          key={side}
          tone="metal"
          size={[0.03, 0.014, 0.05]}
          radius={0.005}
          position={[side * 0.05, y + 0.072, z + 0.012]}
          rotation={[0.35, 0, 0]}
        />
      ))}
      <Slab
        tone="metalDark"
        size={[0.17, 0.05, 0.01]}
        radius={0.004}
        position={[0, y - 0.071, z]}
      />
    </group>
  )
}

/**
 * Inverted 19 L bottle. The glass tone is nearly transparent, so a shell-toned
 * ring around the neck is what actually anchors it to the cabinet in the render.
 */
function Bottle({ base }: { base: number }) {
  const top = BOTTLE_TOP - base

  return (
    <group position={[0, base, 0]}>
      <Lathe
        tone="glass"
        segments={26}
        profileKey="cooler-bottle"
        profile={[
          [0, 0],
          [0.048, 0],
          [0.048, 0.022],
          [0.145, 0.12],
          [0.145, top - 0.05],
          [0.098, top],
          [0, top],
        ]}
      />
      <Cyl tone="shell" r={0.056} h={0.02} segments={22} position={[0, 0.012, 0]} />
    </group>
  )
}

/**
 * Water cooler. Origin at the centre of the footprint, y = 0 on the floor,
 * dispensing niche facing +Z. The accent shows up only as the small button on
 * the fascia, which is exactly the amount of colour this object should carry.
 */
function WaterCooler({ accent, variant = 'bottled' }: AssetContext) {
  const bottled = variant !== 'mains'
  const bodyH = bottled ? BOTTLED_BODY : MAINS_BODY
  const nicheY = bodyH - 0.22

  return (
    <group>
      {/* Recessed plinth, so the cabinet reads as lifted off the floor. */}
      <RBox tone="shellSoft" size={[0.28, PLINTH_H, 0.28]} radius={0.008} position={[0, PLINTH_H / 2, 0]} />
      <RBox
        tone="shell"
        size={[BODY, bodyH - PLINTH_H, BODY]}
        radius={0.02}
        position={[0, PLINTH_H + (bodyH - PLINTH_H) / 2, 0]}
      />

      <DispenseNiche y={nicheY} />

      {/* Accent button, proud of the fascia by 9 mm. */}
      <RBox
        tone="accent"
        color={accent}
        size={[0.06, 0.022, 0.014]}
        radius={0.005}
        position={[0, nicheY + 0.135, FACE + 0.002]}
      />

      {bottled ? (
        <group>
          {/* Collar sunk into the cabinet top so no two faces end up coplanar. */}
          <Cyl tone="shellSoft" r={0.09} h={0.035} segments={24} position={[0, bodyH + 0.01, 0]} />
          <Bottle base={bodyH + 0.02} />
        </group>
      ) : (
        <Slab tone="shellSoft" size={[0.26, 0.26, 0.016]} radius={0.02} position={[0, bodyH + 0.005, 0]} />
      )}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'water-cooler',
  name: 'Dispensador de agua',
  category: 'decor',
  footprint: [0.36, 0.36],
  height: BOTTLE_TOP,
  accentable: true,
  variants: [
    { id: 'bottled', name: 'Con botella' },
    { id: 'mains', name: 'De red' },
  ],
  keywords: ['dispensador', 'agua', 'botellon', 'fuente', 'water cooler', 'dispenser', 'fountain'],
  Component: WaterCooler,
}
